import { defineComponent as _defineComponent } from 'vue'
import SubtitleAndNarrationsUploader from '@/components/videos/SubtitleAndNarrationsUploader.vue';
import { computed, ref, watch } from 'vue';
import { ContentFile, NewVideoV1 } from '@ilteducation/content-registry-types';
import axios from 'axios';
import admin from '@/plugins/admin';
import eventBus from '@/foundations/services/eventBus';

type Props = {
  isCreate: boolean;
  id?: string;
  model: NewVideoV1;
  languageNameFromCode: Function;
  reloadPage: (...args: any[]) => void;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'SubtitlesAndNarrations',
  props: {
    isCreate: { type: Boolean },
    id: null,
    model: null,
    languageNameFromCode: null,
    reloadPage: null
  },
  setup(__props: any) {

const props = __props as {
  isCreate: boolean;
  id?: string;
  model: NewVideoV1;
  languageNameFromCode: Function;
  reloadPage: (...args: any[]) => void;
}



const baseContentUrl = ref<string>();
const downloading = ref(false);
const narrationUrls = ref<Record<string, string>>({});

const narrationsAndSubtitles = computed(() => {
  if (!props.model.narrations || !props.model.subtitles) {
    return {};
  }
  const res: {
    [lang: string]: {
      narration?: ContentFile;
      subtitle?: ContentFile;
    };
  } = {};
  props.model.narrations.forEach((n) => {
    if (res[n.languageCode]) {
      res[n.languageCode].narration = n.value;
    } else {
      res[n.languageCode] = { narration: n.value };
    }
  });
  props.model.subtitles.forEach((s) => {
    if (res[s.languageCode]) {
      res[s.languageCode].subtitle = s.value;
    } else {
      res[s.languageCode] = { subtitle: s.value };
    }
  });
  return res;
});

async function getContentFileUrl(content: ContentFile) {
  if (!baseContentUrl.value) {
    const res = await axios.get(`${process.env.VUE_APP_CONTENT_API_URL}${props.id}/contentUrls?allContent=true`);
    if (!res.data || !res.data.url) {
      return;
    }
    baseContentUrl.value = res.data.url.toString();
  }
  return `${baseContentUrl.value}/${content.fileName}`;
}
async function downloadContentFile(content: ContentFile, lang: string) {
  try {
    downloading.value = true;
    const url = await getContentFileUrl(content);
    if (!url) {
      return;
    }

    // Construct desired download filename with video ID, type, and language
    const extension = content.fileName.split('.').pop() || 'srt';
    const fileType = content.mime_type === 'audio/m4a' ? 'narration' : 'subtitle';
    const downloadName = `${props.model.filmId}_${fileType}_${lang}.${extension}`;

    // Fetch the file as blob and trigger download with custom filename
    // This avoids needing to modify the signed CDN URL
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }
    const blob = await response.blob();

    // Create a blob URL and trigger download
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = downloadName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download failed:', error);
    eventBus.$emit('snackbar.open', 'Failed to download file');
  } finally {
    downloading.value = false;
  }
}

const remove = async (lang: string, file: ContentFile) => {
  if (props.isCreate) {
    return;
  }
  if (!(await admin.confirm(`Are you sure you want to delete this file?`))) {
    return;
  }
  const url = new URL(`${process.env.VUE_APP_CONTENT_API_URL}${props.id}/file`);
  const queryParams = new URLSearchParams();

  if (file.mime_type === 'audio/m4a') {
    queryParams.append('subType', 'narration');
    props.model.narrations = props.model.narrations.filter((n) => n.value?.fileName !== file.fileName);
  } else if (file.extension === 'vtt') {
    queryParams.append('subType', 'subtitles');
    props.model.subtitles = props.model.subtitles.filter((s) => s.value?.fileName !== file.fileName);
  }

  queryParams.append('language', lang);
  queryParams.append('fileName', file.fileName);

  url.search = queryParams.toString();
  const res = await axios.delete(url.toString());
  if (res.status === 200) {
    eventBus.$emit('snackbar.open', 'Content file deleted successfully');
  }
};

watch(
  () => props.model,
  async () => {
    for (const narration of props.model.narrations) {
      if (!narration.value) {
        continue;
      }
      const url = await getContentFileUrl(narration.value);
      if (url) {
        narrationUrls.value[narration.languageCode] = url;
      }
    }
  },
  { immediate: true },
);

return { __sfc: true,props, baseContentUrl, downloading, narrationUrls, narrationsAndSubtitles, getContentFileUrl, downloadContentFile, remove, SubtitleAndNarrationsUploader }
}

})